import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowRight, User } from 'lucide-react';
import Newsletter from '../components/Newsletter';

const blogPosts = [
  {
    id: 1,
    title: 'The Future of AI in Healthcare Diagnostics',
    excerpt: 'Exploring how artificial intelligence is revolutionizing medical diagnostics and improving patient outcomes across the healthcare industry.',
    image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?auto=format&fit=crop&q=80&w=800',
    author: 'Dr. Sarah Chen',
    date: 'Dec 15, 2024',
    readTime: '8 min read',
    category: 'AI & Innovation',
  },
  {
    id: 2,
    title: 'HIPAA Compliance: Best Practices for 2025',
    excerpt: 'A comprehensive guide to maintaining HIPAA compliance in an increasingly digital healthcare environment.',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=800',
    author: 'Thomas Anderson',
    date: 'Dec 10, 2024',
    readTime: '6 min read',
    category: 'Compliance',
  },
  {
    id: 3,
    title: 'Telehealth Integration: Lessons Learned',
    excerpt: 'Key insights from implementing telehealth solutions across multiple healthcare organizations.',
    image: 'https://images.unsplash.com/photo-1609904603780-71c3d1a108a3?auto=format&fit=crop&q=80&w=800',
    author: 'Michael Roberts',
    date: 'Dec 5, 2024',
    readTime: '7 min read',
    category: 'Telehealth',
  },
  {
    id: 4,
    title: 'Optimizing EHR Systems for Better Patient Care',
    excerpt: 'Strategies for maximizing the effectiveness of Electronic Health Record systems in clinical settings.',
    image: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=800',
    author: 'Dr. Emily Watson',
    date: 'Nov 28, 2024',
    readTime: '9 min read',
    category: 'EHR Systems',
  },
  {
    id: 5,
    title: 'Cybersecurity Threats in Healthcare: 2024 Report',
    excerpt: 'An analysis of the latest cybersecurity threats facing healthcare organizations and how to protect against them.',
    image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&q=80&w=800',
    author: 'James Liu',
    date: 'Nov 20, 2024',
    readTime: '10 min read',
    category: 'Security',
  },
  {
    id: 6,
    title: 'Training Healthcare Staff on New Technologies',
    excerpt: 'Effective approaches to technology training that ensure smooth adoption and minimize workflow disruption.',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=800',
    author: 'Dr. Rachel Green',
    date: 'Nov 15, 2024',
    readTime: '5 min read',
    category: 'Training',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Blog() {
  return (
    <div className="min-h-screen pt-20" data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
              Our Blog
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              Healthcare Tech Insights
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Stay informed with the latest trends, best practices, and innovations in healthcare technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-12" data-testid="featured-post-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-8 items-center bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100"
          >
            <div className="relative h-[400px]">
              <img
                src={blogPosts[0].image}
                alt={blogPosts[0].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="px-4 py-1.5 bg-cyan-500 text-white text-sm font-medium rounded-full">
                  Featured
                </span>
              </div>
            </div>
            <div className="p-8 lg:p-12">
              <span className="text-sm font-medium text-cyan-600 mb-3 block">
                {blogPosts[0].category}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                {blogPosts[0].title}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {blogPosts[0].excerpt}
              </p>
              <div className="flex items-center gap-6 text-sm text-slate-500 mb-6">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {blogPosts[0].author}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {blogPosts[0].date}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {blogPosts[0].readTime}
                </div>
              </div>
              <Button 
                className="btn-gradient text-white rounded-full px-6 py-3 font-medium shadow-lg shadow-cyan-500/20"
                data-testid="featured-post-cta"
              >
                Read Article
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20" data-testid="blog-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {blogPosts.slice(1).map((post, index) => (
              <motion.article
                key={post.id}
                variants={itemVariants}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-shadow duration-300"
                data-testid={`blog-post-${index}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-cyan-600 text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      {post.readTime}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          <div className="text-center mt-12">
            <Button 
              variant="outline"
              className="rounded-full px-8 py-4 font-medium border-slate-200 hover:bg-slate-50"
              data-testid="load-more-posts"
            >
              Load More Articles
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
